/**
 * QC review query hooks.
 *
 * Mirrors the use-ingestion-runs.ts shape: useState/useCallback/useEffect
 * pattern with `useInvalidationSubscription` so any QC mutation triggers an
 * automatic refetch.
 *
 * Companion file: `use-qc-mutations.ts` (runQc / acknowledge / forceRerun /
 * exportRun). Kept in separate files so each hook fits the eslint
 * max-lines-per-function budget (75).
 */

import { useCallback, useEffect, useState } from 'react'

import { getLatestQcRun, listQcRuns } from '../services/qc'
import type { QcRun, QcRunWithResults } from '../types/qc'
import { useInvalidationSubscription } from './use-list-invalidation'

/** Cache key for QC invalidations — mutations call `invalidate(['qc_runs'])`. */
export const QC_RUNS_INVALIDATION_KEY = 'qc_runs'

// ---------------------------------------------------------------------------
// Latest QC run for an ingestion
// ---------------------------------------------------------------------------

interface UseQcLatestRunResult {
  /** `null` while loading or when no QC has been run yet on the ingestion. */
  latest: QcRunWithResults | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useQcLatestRun(
  caseId: string | null,
  runId: string | null
): UseQcLatestRunResult {
  const [latest, setLatest] = useState<QcRunWithResults | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId || !runId) {
      setLatest(null)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await getLatestQcRun(caseId, runId)
      setLatest(data)
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : 'Failed to fetch QC run')
      setLatest(null)
    } finally {
      setLoading(false)
    }
  }, [caseId, runId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  useInvalidationSubscription(QC_RUNS_INVALIDATION_KEY, fetch)

  return { latest, loading, error, refetch: fetch }
}

// ---------------------------------------------------------------------------
// Historical list of QC runs for an ingestion (audit view)
// ---------------------------------------------------------------------------

interface UseQcRunHistoryResult {
  runs: QcRun[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useQcRunHistory(
  caseId: string | null,
  runId: string | null
): UseQcRunHistoryResult {
  const [runs, setRuns] = useState<QcRun[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId || !runId) {
      setRuns([])
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const response = await listQcRuns(caseId, runId)
      setRuns(response.runs)
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : 'Failed to fetch QC run history')
      setRuns([])
    } finally {
      setLoading(false)
    }
  }, [caseId, runId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  useInvalidationSubscription(QC_RUNS_INVALIDATION_KEY, fetch)

  return { runs, loading, error, refetch: fetch }
}
